import { useAllSalebills, useViewSaleBillReport } from "http-hooks/salebill/useSalebill";
import DeleteSaleBill from "components/saleBill/DeleteSaleBill";
import { useState } from "react";
import useTableSort, { withSort } from "hooks/useTableSort";
import { toIndianCurrencyFormat } from "../utils/amount";
import { formatDateAndTime } from "utils/date";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getDocumentUrls, getDocumentViewerRoute } from "utils/documentUrlHelper";
import useAuth from "hooks/useAuth";
import { SelectVendors, SelectCustomers } from "components/ui/selectors";
import AsyncItemSelect from "components/item/AsyncItemSelect";
import { ActionButton } from "components/ui/Table";
import InputSearch from "components/ui/InputSearch";
import {
  PencilSquareIcon,
  TrashIcon,
  EyeIcon,
} from "@heroicons/react/24/outline";
import { getBillTypeWord } from "utils/salebill";
import MasterPage, {
  FilterRow,
  FilterCell,
  ClearFiltersButton,
  AddButton,
  DeleteAction,
} from "components/ui/MasterPage";

const initialFilter = {
  salebillno: "",
  cusid: "",
  vendid: "",
  itemIds: "",
  ignore_pl: 1,
  page: 1,
  limit: 10,
};

const columns = [
  { key: "salebillno", header: "Bill No.", sortable: true },
  {
    key: "salebilldate",
    header: "Bill Date",
    sortable: true,
    render: (item) => formatDateAndTime(item.salebilldate),
  },
  {
    key: "billtype",
    header: "Bill Type",
    sortable: true,
    render: (item) => getBillTypeWord(item.billtype),
  },
  {
    key: "customer",
    header: "Customer",
    sortable: true,
    render: (item) =>
      item.customer ? (
        <div className="font-medium">{item.customer.name}</div>
      ) : (
        item.cusid || "-"
      ),
  },
  {
    key: "vendor",
    header: "Vendor",
    sortable: true,
    render: (item) =>
      item.vendor ? (
        <div className="font-medium">{item.vendor.name}</div>
      ) : (
        item.vendid || "-"
      ),
  },
  {
    key: "salebillvalue",
    header: "Bill Value",
    sortable: true,
    render: (item) => toIndianCurrencyFormat(item.salebillvalue),
  },
  { key: "actions", header: "Actions", isActions: true },
];

export default function Salebill() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { agttp } = useAuth();
  const requestedTraderBillType = searchParams.get("traderbilltyp");
  const traderBillType =
    agttp === 1 && ["P", "S"].includes(requestedTraderBillType)
      ? requestedTraderBillType
      : agttp === 1
        ? "S"
        : "G";
  const billName =
    agttp === 1 ? (traderBillType === "P" ? "Purchase Bill" : "Sale Bill") : "Sale Bill";
  const traderBillQuery =
    agttp === 1 ? `?traderbilltyp=${traderBillType}` : "";
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [selectedItems, setSelectedItems] = useState([]);
  const [viewingReportId, setViewingReportId] = useState(null);
  const [filter, setFilter] = useState(initialFilter);
  const [searchSalebill, setSearchSalebill] = useState("");
  const tableSort = useTableSort();

  const { mutateAsync: viewReport } = useViewSaleBillReport();

  const handleCustomerChange = (customer) => {
    setSelectedCustomer(customer);
    setFilter((prev) => ({
      ...prev,
      cusid: customer?.ps_Id || "",
    }));
  };

  const handleVendorChange = (vendor) => {
    setSelectedVendor(vendor);
    setFilter((prev) => ({
      ...prev,
      vendid: vendor?.ps_Id || "",
    }));
  };

  const handleItemsChange = (items) => {
    setSelectedItems(items);
    setFilter((prev) => ({
      ...prev,
      itemIds: items.map((item) => item.value).join(","),
    }));
  };

  const handleSalebillSearch = (searchTerm) => {
    setFilter((prev) => ({
      ...prev,
      salebillno: searchTerm,
    }));
  };

  const clearFilters = () => {
    setSelectedCustomer(null);
    setSelectedVendor(null);
    setSelectedItems([]);
    setSearchSalebill("");
    setFilter(initialFilter);
  };

  const handleEditSaleBill = (id) => {
    id = parseInt(id);
    navigate(`/salebill/${id}${traderBillQuery}`);
  };

  const handleViewReport = async (bill) => {
    try {
      setViewingReportId(bill.salebillid);
      const data = await viewReport(bill.salebillid);

      if (data.htmlUrl) {
        const { htmlUrl, pdfUrl } = getDocumentUrls(data);

        const route = getDocumentViewerRoute(bill.salebillid, {
          htmlUrl,
          pdfUrl,
          documentNumber: bill.salebillno,
          documentType: data.documentTitle || billName,
        });

        navigate(route);
      }
    } catch (error) {
      console.error("Error viewing report:", error);
    } finally {
      setViewingReportId(null);
    }
  };

  const {
    isLoading,
    data: { data: allsalebills, page: currentPage, totalPages } = [],
  } = useAllSalebills(
    withSort({ ...filter, traderbilltyp: traderBillType }, tableSort)
  );

  return (
    <MasterPage
      title={billName}
      headerActions={
        <AddButton onClick={() => navigate(`/salebill/new${traderBillQuery}`)}>
          Add {billName}
        </AddButton>
      }
      filters={
        <>
          <FilterRow>
            <FilterCell>
              <InputSearch
                value={searchSalebill}
                onChange={(e) => setSearchSalebill(e.target.value)}
                onSearch={handleSalebillSearch}
                label={`${billName} No.`}
                placeholder={`Search by ${billName.toLowerCase()} number`}
              />
            </FilterCell>
            {(agttp !== 1 || traderBillType === "S") && (
              <FilterCell>
                <SelectCustomers
                  value={selectedCustomer}
                  onChange={handleCustomerChange}
                  label="Customer"
                  placeholder="Select Customer"
                />
              </FilterCell>
            )}
            {(agttp !== 1 || traderBillType === "P") && (
              <FilterCell>
                <SelectVendors
                  value={selectedVendor}
                  onChange={handleVendorChange}
                  label="Vendor"
                  placeholder="Select Vendor"
                />
              </FilterCell>
            )}
            <FilterCell>
              <AsyncItemSelect
                value={selectedItems}
                onChange={handleItemsChange}
                label="Items"
                placeholder="Search items..."
                isMulti={true}
              />
            </FilterCell>
          </FilterRow>
          <FilterRow>
            <ClearFiltersButton onClick={clearFilters} />
          </FilterRow>
        </>
      }
      columns={columns}
      sort={tableSort}
      data={allsalebills}
      isLoading={isLoading}
      rowKey="salebillid"
      deleteComponent={DeleteSaleBill}
      renderActions={(salebill, setDeleteItemId) => (
        <>
          <ActionButton
            variant="primary"
            onClick={() => handleViewReport(salebill)}
            title="View Report"
            disabled={viewingReportId === salebill.salebillid}
          >
            {viewingReportId === salebill.salebillid ? (
              <svg
                className="animate-spin h-4 w-4"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
            ) : (
              <EyeIcon className="h-4 w-4" aria-hidden="true" />
            )}
          </ActionButton>
          <ActionButton
            variant="secondary"
            onClick={() => handleEditSaleBill(salebill.salebillid)}
            title={`Edit ${billName}`}
          >
            <PencilSquareIcon className="h-4 w-4" aria-hidden="true" />
          </ActionButton>
          <DeleteAction
            onClick={() => setDeleteItemId(salebill.salebillid)}
            title={`Delete ${billName}`}
          />
        </>
      )}
      pagination={{
        currentPage,
        totalPages,
        onPageChange: (page) => setFilter((prev) => ({ ...prev, page })),
      }}
    />
  );
}
